import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { motion, useTransform, useViewportScroll } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useWindowSize } from "../../../../hooks/windowSize";

interface ColumnBlockProps {
  headlineOne?: String;
  headlineTwo?: String;
  colOnesubHeader: String;
  colOneP1: String;
  colTwosubHeader: String;
  colTwoP1: String;
  colThreesubHeader: String;
  colThreeP1: String;
  colFoursubHeader: String;
  colFourP1: String;
  bgColor: String;
  bgImg?: String;
  bgPositionDesktop?: String;
  bgPositionMobile?: String;
  color:
    | "inherit"
    | "initial"
    | "primary"
    | "secondary"
    | "textPrimary"
    | "textSecondary"
    | "error";
}

const useStyles = makeStyles((theme) => ({
  mainWrapperColCenter: (props: ColumnBlockProps) => ({
    backgroundColor: `${props.bgColor}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundPosition: `${props.bgPositionDesktop}`,
    backgroundSize: "cover",
    backgroundImage: `linear-gradient(25deg, rgba(31,40,51,0.7) 20%, rgba(31,40,51,0) 55%),url(${props.bgImg})`,
    height: "75vh",
    padding: theme.spacing(18),
    [theme.breakpoints.down("lg")]: {
      height: "75vh",
      paddingTop: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingBottom: theme.spacing(0),
      paddingLeft: theme.spacing(10),
    },
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(6),
    },
    [theme.breakpoints.down("sm")]: {
      backgroundPosition: `${props.bgPositionMobile}`,
      height: "auto",
      padding: theme.spacing(6),
    },
    [theme.breakpoints.down("xs")]: {
      height: "150vh",
      paddingTop: theme.spacing(5),
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(6),
      paddingLeft: theme.spacing(4),
    },
  }),

  headlineWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  bodyWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: "12px",
    [theme.breakpoints.down("xs")]: {},
  },
  h1bas: {
    fontSize: "72px !important",
    lineHeight: "72px !important",
    letterSpacing: -2,
    [theme.breakpoints.down("lg")]: {
      fontSize: "60px !important",
      letterSpacing: -2,
      lineHeight: "60px !important",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "60px !important",
      letterSpacing: -2,
      lineHeight: "60px !important",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "48px !important",
      letterSpacing: -2,
      lineHeight: "48px !important",
    },
  },
  h3bas: {
    fontSize: "24px",
    lineHeight: "28px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "24px",
      lineHeight: "28px",
    },
  },
  body1bas: {
    fontSize: "18px",
    lineHeight: "28px",
    fontWeight: 600,
    color: "#ffffff",
    [theme.breakpoints.down("xs")]: {
      fontSize: "18px",
      lineHeight: "28px",
    },
  },
}));

export const ColumnBlock: React.FC<ColumnBlockProps> = (props) => {
  const {
    headlineOne,
    headlineTwo,
    colOnesubHeader,
    colOneP1,
    colTwosubHeader,
    colTwoP1,
    colThreesubHeader,
    colThreeP1,
    colFoursubHeader,
    colFourP1,
    color,
  } = props;
  const classes = useStyles(props);
  const size = useWindowSize();
  const headlineRef = useRef(null);
  const paragraphRef = useRef(null);
  const { scrollY } = useViewportScroll();
  const [headlineYPosition, setHeadlineYPosition] = useState<number>(0);
  const [paragraphYPosition, setParagraphYPosition] = useState<number>(0);

  useEffect(() => {
    if (headlineRef.current && paragraphRef.current) {
      setHeadlineYPosition(headlineRef.current.offsetTop);
      setParagraphYPosition(paragraphRef.current.offsetTop);
    }
  }, [headlineRef.current, paragraphRef.current]);

  const headlineTransform = useTransform(
    scrollY,
    [
      headlineYPosition - size.innerHeight * 0.9,
      headlineYPosition - size.innerHeight * 0.6,
    ],
    [60, 0]
  );

  const headlineOpacity = useTransform(
    scrollY,
    [
      headlineYPosition - size.innerHeight * 0.9,
      headlineYPosition - size.innerHeight * 0.6,
    ],
    [0.1, 1]
  );

  const paragraphTransform = useTransform(
    scrollY,
    [
      paragraphYPosition - size.innerHeight * 0.91,
      paragraphYPosition - size.innerHeight * 0.49,
    ],
    [80, 0]
  );

  const paragraphOpacity = useTransform(
    scrollY,
    [
      paragraphYPosition - size.innerHeight * 0.91,
      paragraphYPosition - size.innerHeight * 0.49,
    ],
    [0.1, 1]
  );

  return (
    <Grid item className={classes.mainWrapperColCenter} xs={12} md={12}>
      <motion.div
        style={{
          y: headlineTransform,
          opacity: headlineOpacity,
          display: "flex",
        }}
        ref={headlineRef}
      >
        <Box className={classes.headlineWrapper}>
          <Typography variant="h1" className={classes.h1bas} color={color}>
            {headlineOne}
          </Typography>
          <Typography variant="h1" className={classes.h1bas} color={color}>
            {headlineTwo}
          </Typography>
        </Box>
      </motion.div>
      <motion.div
        style={{ y: paragraphTransform, opacity: paragraphOpacity }}
        ref={paragraphRef}
      >
        <Grid container spacing={6}>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            className={classes.bodyWrapper}
          >
            <Typography variant="h4" className={classes.h3bas} color={color}>
              {colOnesubHeader}
            </Typography>
            <Typography variant="body1" className={classes.body1bas}>
              {colOneP1}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            className={classes.bodyWrapper}
          >
            <Typography variant="h4" className={classes.h3bas} color={color}>
              {colTwosubHeader}
            </Typography>
            <Typography variant="body1" className={classes.body1bas}>
              {colTwoP1}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            className={classes.bodyWrapper}
          >
            <Typography variant="h4" className={classes.h3bas} color={color}>
              {colThreesubHeader}
            </Typography>
            <Typography variant="body1" className={classes.body1bas}>
              {colThreeP1}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            className={classes.bodyWrapper}
          >
            <Typography variant="h4" className={classes.h3bas} color={color}>
              {colFoursubHeader}
            </Typography>
            <Typography variant="body1" className={classes.body1bas}>
              {colFourP1}
            </Typography>
          </Grid>
        </Grid>
      </motion.div>
    </Grid>
  );
};
